import React, { useState, useEffect, useMemo, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import Sidenav from "../components/Sidenav";
import CreateOrder from "../components/CreateOrder";
import CreateOrderAssignment from "../components/CreateOrderAssignment";
import DeleteOrderAssignmentModal from "../components/DeleteOrderAssignmentModal";
import "../Console.css";

const styles = (
  <style>{`
    .search-wrap {
      position: sticky;
      top: 0;
      z-index: 10;
      background: #f3f4f6;
      padding: 12px 20px 10px 20px;
    }
    .search-bar {
      display:flex; align-items:center; gap:10px;
      background:#fff; border:1.5px solid #e5e7eb; border-radius:9999px;
      padding:10px 16px; box-shadow:0 1px 3px rgba(0,0,0,0.05);
      transition: border-color 0.15s, box-shadow 0.15s;
    }
    .search-bar.focused {
      border-color: #ff5100;
      box-shadow: 0 0 0 3px rgba(255,81,0,0.1);
    }
    .search-icon {
      color: #9ca3af; font-size: 14px; flex-shrink: 0;
      transition: color 0.15s;
    }
    .search-bar.focused .search-icon { color: #ff5100; }
    .search-input {
      flex:1; border:none; outline:none; background:transparent; font-size:14px; color:#111827;
    }
    .search-input::placeholder { color: #9ca3af; }
    .search-shortcut {
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 11px; font-weight: 700;
      background: #f3f4f6; border: 1px solid #e5e7eb; border-bottom-width: 2px;
      border-radius: 5px; padding: 1px 6px; color: #6b7280;
      user-select: none; flex-shrink: 0;
    }
    .search-clear {
      border: 1px solid #e5e7eb; background:#f3f4f6; color:#374151;
      padding:3px 10px; border-radius:9999px; font-size:12px; font-weight:700;
      flex-shrink: 0; cursor: pointer;
    }
    .search-clear:hover { background: #e5e7eb; }

    /* Order cards */
    .order-card {
      background: #fff;
      border-radius: 18px;
      border: 1px solid #f3f4f6;
      box-shadow: 0 1px 4px rgba(0,0,0,0.05);
      overflow: hidden;
      margin-bottom: 16px;
      transition: box-shadow 0.15s;
    }
    .order-card:hover {
      box-shadow: 0 4px 16px rgba(0,0,0,0.08);
    }
    .order-card-header {
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 16px 20px;
      border-bottom: 1px solid #f9fafb;
      flex-wrap: wrap;
    }
    .order-card-icon {
      width: 40px; height: 40px; border-radius: 11px; flex-shrink: 0;
      background: rgba(255,81,0,0.08); border: 1.5px solid rgba(255,81,0,0.2);
      display: flex; align-items: center; justify-content: center;
    }
    .order-card-title {
      font-size: 15px; font-weight: 800; color: #111827; line-height: 1.2;
    }
    .order-card-sub {
      font-size: 12px; color: #9ca3af; margin-top: 2px;
    }
    .order-pill {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 4px 10px; border-radius: 9999px;
      font-size: 11px; font-weight: 700;
      letter-spacing: 0.3px;
    }
    .order-pill-orange {
      background: rgba(255,81,0,0.08); border: 1.5px solid rgba(255,81,0,0.2);
      color: #c13d00;
    }
    .order-pill-grey {
      background: #f3f4f6; border: 1.5px solid #e5e7eb; color: #374151;
    }
    .order-pill-amber {
      background: #fffbeb; border: 1.5px solid #fcd34d; color: #b45309;
    }

    /* Metadata grid */
    .order-meta-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
      gap: 0;
    }
    .order-meta-cell {
      padding: 14px 20px;
      border-right: 1px solid #f3f4f6;
      border-bottom: 1px solid #f3f4f6;
    }
    .order-meta-cell:last-child { border-right: none; }
    .order-meta-label {
      font-size: 10.5px; font-weight: 700; color: #9ca3af;
      text-transform: uppercase; letter-spacing: 0.5px;
      display: flex; align-items: center; gap: 5px;
      margin-bottom: 5px;
    }
    .order-meta-label i { font-size: 10px; width: 11px; text-align: center; }
    .order-meta-value {
      font-size: 13.5px; color: #111827; font-weight: 600;
      word-break: break-word; overflow-wrap: anywhere;
    }
    .order-desc-cell {
      padding: 14px 20px;
      border-bottom: 1px solid #f3f4f6;
      grid-column: 1 / -1;
    }

    /* Card footer / actions */
    .order-card-actions {
      display: flex; align-items: center; gap: 10px;
      padding: 14px 20px; flex-wrap: wrap;
    }
    .btn-action {
      display: inline-flex; align-items: center; gap: 7px;
      padding: 8px 18px; border-radius: 9999px;
      font-size: 13px; font-weight: 700; cursor: pointer;
      border: 1.5px solid; transition: background 0.15s, box-shadow 0.15s;
      text-decoration: none;
    }
    .btn-action:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
    .btn-action-toggle {
      background: #f9fafb; border-color: #e5e7eb; color: #374151;
    }
    .btn-action-toggle:hover { background: #f3f4f6; }
    .btn-action-toggle.active {
      background: rgba(255,81,0,0.07); border-color: rgba(255,81,0,0.3); color: #c13d00;
    }
    .btn-action-assign {
      background: rgba(5,150,105,0.07); border-color: rgba(5,150,105,0.3); color: #065f46;
    }
    .btn-action-assign:hover { background: rgba(5,150,105,0.12); }
    .btn-action-invoice {
      background: rgba(255,81,0,0.07); border-color: rgba(255,81,0,0.25); color: #c13d00;
    }
    .btn-action-invoice:hover { background: rgba(255,81,0,0.12); }
    .btn-action-ml { margin-left: auto; }

    /* Assignments panel */
    .assignments-panel {
      border-top: 1px solid #f3f4f6;
      background: #fafafa;
      padding: 16px 20px;
    }
    .assignments-table {
      width: 100%; border-collapse: separate; border-spacing: 0;
      font-size: 13px;
    }
    .assignments-table thead th {
      font-size: 10.5px; font-weight: 700; color: #9ca3af;
      text-transform: uppercase; letter-spacing: 0.5px;
      padding: 0 12px 10px; border-bottom: 1.5px solid #e5e7eb;
      white-space: nowrap;
    }
    .assignments-table thead th:first-child { padding-left: 0; }
    .assignments-table tbody tr:not(:last-child) td {
      border-bottom: 1px solid #f3f4f6;
    }
    .assignments-table tbody td {
      padding: 11px 12px; color: #374151; vertical-align: top;
    }
    .assignments-table tbody td:first-child { padding-left: 0; }
    .serial-mono {
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 12px; background: #f3f4f6; border-radius: 6px;
      padding: 2px 7px; color: #374151; display: inline-block;
    }
    .client-name {
      font-weight: 700; color: #111827;
    }
    .asgn-actions { position: relative; display: inline-block; }
    .asgn-ellipsis {
      display: inline-flex; align-items: center; justify-content: center;
      width: 32px; height: 32px; border-radius: 8px;
      background: #fff; color: #111827; border: 1px solid #e5e7eb;
      font-size: 13px; cursor: pointer;
    }
    .asgn-ellipsis:hover { background: #f9fafb; }
    .asgn-menu {
      position: absolute; bottom: calc(100% + 4px); top: auto; right: 0;
      background: #fff; border: 1px solid #e5e7eb; border-radius: 12px;
      box-shadow: 0 10px 30px rgba(0,0,0,0.08); min-width: 140px;
      padding: 6px; z-index: 50;
    }
    .asgn-menu-item {
      width: 100%; display: flex; align-items: center; gap: 10px;
      border: none; background: transparent; text-align: left;
      padding: 8px 10px; border-radius: 8px;
      font-size: 13px; color: #111827; font-weight: 700; cursor: pointer;
    }
    .asgn-menu-item:hover { background: #f3f4f6; }
    .asgn-menu-item.danger { color: #b91c1c; }
    .asgn-menu-item.danger:hover { background: #fff1f2; }
    .assignments-empty {
      padding: 24px 0; text-align: center;
      color: #9ca3af; font-size: 13px;
    }
    .assignments-empty i { font-size: 20px; margin-bottom: 8px; display: block; }
    .pricing-breakdown { font-size: 12px; }
    .pricing-line { color: #6b7280; }
    .pricing-total {
      font-weight: 700; color: #111827;
      border-top: 1px solid #e5e7eb; margin-top: 4px; padding-top: 4px;
    }
  `}</style>
);

const BleSTOrders = ({ domain, userData }) => {
  const navigate = useNavigate();

  const [modalData, setModalData] = useState({ order: null, assignments: [] });
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [showCreateOrderAssignmentModal, setShowCreateOrderAssignmentModal] = useState(false);
  const [expandedOrderIndex, setExpandedOrderIndex] = useState(null);
  const [tagOrders, setTagOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const searchInputRef = useRef(null);

  const handleCloseCreateOrderModal = () => setShowCreateOrderModal(false);
  const handleShowCreateOrderModal = () => setShowCreateOrderModal(true);
  const handleCloseCreateOrderAssignmentModal = () => setShowCreateOrderAssignmentModal(false);
  const handleShowCreateOrderAssignmentModal = () => setShowCreateOrderAssignmentModal(true);

  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [showDeleteAssignmentModal, setShowDeleteAssignmentModal] = useState(false);
  const [deleteAssignment, setDeleteAssignment] = useState(null);
  const [orgLogoMap, setOrgLogoMap] = useState({});
  const [openMenuId, setOpenMenuId] = useState(null);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "/") return;
      const tag = document.activeElement?.tagName?.toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (showCreateOrderModal || showCreateOrderAssignmentModal || showDeleteAssignmentModal) return;
      e.preventDefault();
      searchInputRef.current?.focus();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [showCreateOrderModal, showCreateOrderAssignmentModal, showDeleteAssignmentModal]);

  useEffect(() => {
    const close = () => setOpenMenuId(null);
    const onKey = (e) => { if (e.key === "Escape") setOpenMenuId(null); };
    document.addEventListener("click", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const handleCreateAssignment = (orderIndex) => {
    const order = tagOrders[orderIndex];
    if (!order || !order._id) { alert("Invalid order selected."); return; }
    setSelectedOrderId(order._id);
    setShowCreateOrderAssignmentModal(true);
    setModalData({ order, assignments: order.assignments });
  };

  const toggleExpanded = (index) => {
    setExpandedOrderIndex(expandedOrderIndex === index ? null : index);
  };

  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      const [ordersRes, assignmentsRes, orgsRes] = await Promise.all([
        fetch("https://api.iriscrypt.com/order", {
          headers: { "x-api-key": "f87f030d-eaf4-4a39-9d5d-c9ffdb90ab43" },
        }),
        fetch("https://api.iriscrypt.com/orderAssignment", {
          headers: { "x-api-key": "f87f030d-eaf4-4a39-9d5d-c9ffdb90ab43" },
        }),
        fetch("/api/organizations"),
      ]);
      const data = await ordersRes.json();
      const allAssignments = await assignmentsRes.json();
      const orgs = await orgsRes.json();
      const logoMap = {};
      (orgs || []).forEach((o) => {
        if (o.organization && (o.logoUrl || o.imageUrl)) {
          logoMap[o.organization] = o.logoUrl || o.imageUrl;
        }
      });
      setOrgLogoMap(logoMap);
      const assignmentMap = {};
      (allAssignments || []).forEach((a) => { assignmentMap[a._id] = a; });
      const formatted = data.map((order) => ({
        _id: order._id,
        date: order.timestamp.split("T")[0],
        type: order.metadata?.type || "—",
        length: order.metadata?.length && order.metadata.length !== 0
          ? `${order.metadata.length}mm` : "—",
        size: order.metadata?.size || "—",
        purpose: order.metadata?.purpose
          ? order.metadata.purpose.charAt(0).toUpperCase() + order.metadata.purpose.slice(1)
          : "—",
        quantity: order.quantity,
        serialRange: `${order.minSerial} - ${order.maxSerial}`,
        description: order.metadata?.description || "—",
        status: "Pending Authorization",
        assignments: (order.assignments || []).map((a) => assignmentMap[a._id] || a),
      }));
      setTagOrders(formatted);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  const handleFormSubmit = () => { fetchOrders(); };

  const handleDeleteAssignment = (assignment) => {
    setDeleteAssignment(assignment);
    setShowDeleteAssignmentModal(true);
  };

  const filteredOrders = useMemo(() => {
    if (!searchTerm.trim()) return tagOrders;
    const q = searchTerm.toLowerCase();
    const matches = (str) => typeof str === "string" && str.toLowerCase().includes(q);
    return tagOrders.filter((o) => {
      const baseMatch =
        matches(o.date) || matches(o.type) || matches(o.length) ||
        matches(String(o.quantity)) || matches(o.serialRange) ||
        matches(o.purpose) || matches(o.description) || matches(o.status);
      if (baseMatch) return true;
      return o.assignments.some((a) =>
        matches(`${a.minSerial} - ${a.maxSerial}`) ||
        matches(a.client?.name || "") ||
        matches(a.description || "") ||
        matches(new Date(a.date).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }))
      );
    });
  }, [searchTerm, tagOrders]);

  const renderPricing = (a) => {
    if (!a.currency) return <span style={{ color: "#9ca3af" }}>—</span>;
    const qty = a.maxSerial - a.minSerial + 1;
    const unit = parseFloat(a.unitCost) || 0;
    const addons = (a.additionalCosts || []).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
    const subtotal = unit * qty + addons;
    const afterDiscount = subtotal * (1 - (parseFloat(a.discount) || 0) / 100);
    const total = afterDiscount * (1 + (parseFloat(a.tax) || 0) / 100);
    const fmt = (n) => Number.isFinite(n) ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00";
    return (
      <div className="pricing-breakdown">
        {a.unitCost != null && a.unitCost !== "" && (
          <div className="pricing-line">Unit: {a.currency} {fmt(parseFloat(a.unitCost))} × {qty.toLocaleString()}</div>
        )}
        {(a.additionalCosts || []).filter(c => c.label && c.amount !== "").map((c, i) => (
          <div key={i} className="pricing-line">{c.label}: {a.currency} {fmt(parseFloat(c.amount) || 0)}</div>
        ))}
        {parseFloat(a.discount) > 0 && (
          <div className="pricing-line">Discount: {a.discount}%</div>
        )}
        {parseFloat(a.tax) > 0 && (
          <div className="pricing-line">Tax: {a.tax}%</div>
        )}
        <div className="pricing-total">{a.currency} {fmt(total)}</div>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Tag Orders | BLEEP</title>
        <meta
          name="description"
          content="Introducing BLEEP, the revolutionary crowdsourcing app designed to combat counterfeit products and services."
        />
      </Helmet>

      {styles}

      <div className="d-flex">
        <Sidenav domain={domain} />
        <div style={{ flex: 1, height: "calc(100vh - 94px)", overflowY: "auto" }}>

          {/* Header — preserved as-is */}
          <header style={{
            position: "relative", paddingInline: "30px", paddingBlock: "30px",
            color: "#fff",
            background: "linear-gradient(135deg, rgba(0, 0, 0, 1) 0%, rgba(255, 98, 0, 1) 100%)",
            overflow: "hidden",
          }}>
            <div aria-hidden style={{
              position: "absolute", inset: 0,
              backgroundImage: "url('/images/background-art.jpg')",
              backgroundRepeat: "repeat", backgroundSize: "400px 400px",
              backgroundPosition: "center", mixBlendMode: "overlay",
              opacity: 0.05, zIndex: 0, pointerEvents: "none",
            }} />
            <div className="row d-flex align-items-center" style={{ position: "relative", zIndex: 1 }}>
              <div className="col" style={{ padding: 10 }}>
                <div className="col d-flex align-items-center">
                  <b style={{ fontSize: "30px", textShadow: "#1c1c1c 1px 2px 0px" }}>
                    <i className="fas fa-shopping-cart"></i> BleST Tag Orders
                  </b>
                </div>
                <p style={{ paddingTop: "10px", textShadow: "#1c1c1c 1px 1px 0px" }}>
                  Manage tag orders with IrisCrypt Inc.
                </p>
                <div style={{ alignItems: "center", display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    className="btn btn-light btn-lg"
                    style={{ borderRadius: "30px", boxShadow: "rgba(0, 0, 0, 0.25) 0px 3px 8px", paddingBlock: "10px", paddingInline: "30px" }}
                    onClick={handleShowCreateOrderModal}
                  >
                    <span style={{ marginRight: "10px" }}><i className="fa-solid fa-plus-circle"></i></span>
                    <b>Place order</b>
                  </button>
                  <button
                    type="button"
                    className="btn btn-light btn-lg"
                    style={{ borderRadius: "30px", boxShadow: "rgba(0, 0, 0, 0.25) 0px 3px 8px", paddingBlock: "10px", paddingInline: "30px" }}
                    onClick={() => navigate("/irisstatementofaccounts")}
                  >
                    <span style={{ marginRight: "10px" }}><i className="fa-solid fa-file-invoice"></i></span>
                    <b>Statement of Accounts</b>
                  </button>
                </div>
              </div>
            </div>
          </header>

          {/* Search */}
          <div style={{ backgroundColor: "#f3f4f6" }}>
            <div className="search-wrap">
              <div className={`search-bar${searchFocused ? " focused" : ""}`} role="search">
                <i className="fas fa-search search-icon" aria-hidden="true" />
                <input
                  ref={searchInputRef}
                  className="search-input"
                  type="search"
                  placeholder="Search orders or assignments…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  aria-label="Search orders or assignments"
                />
                {!searchTerm && <kbd className="search-shortcut" title="Press / to focus">/</kbd>}
                {searchTerm && (
                  <button
                    className="search-clear"
                    onClick={() => { setSearchTerm(""); searchInputRef.current?.focus(); }}
                    title="Clear search"
                  >Clear</button>
                )}
              </div>
            </div>
          </div>

          <div style={{ padding: "20px", backgroundColor: "#f3f4f6" }}>

            {/* Tip banner */}
            <div style={{
              display: "flex", alignItems: "center", gap: 14,
              background: "#fff7f3", border: "1.5px solid rgba(255,81,0,0.2)",
              borderRadius: 14, padding: "16px 18px", marginBottom: 20,
              boxShadow: "0 1px 4px rgba(255,81,0,0.06)",
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                background: "rgba(255,81,0,0.1)", border: "1.5px solid rgba(255,81,0,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <i className="fa-solid fa-lightbulb" style={{ color: "#ff5100", fontSize: 15 }} />
              </div>
              <div style={{ lineHeight: 1.65, fontSize: 13.5, color: "#374151" }}>
                <span style={{ fontWeight: 800, color: "#c13d00", fontSize: 13 }}>Tip</span>
                <span style={{ color: "#9ca3af", margin: "0 6px" }}>·</span>
                Orders placed with IrisCrypt Inc. will appear below. You MUST further segment and
                assign these serial ranges to link them with specific clients and to activate the tags.
              </div>
            </div>

            {isLoading ? (
              <div style={{
                minHeight: "240px", display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", color: "#6b7280", gap: 12,
              }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: "28px" }} aria-hidden="true" />
                <div style={{ fontSize: 13 }}>Loading orders…</div>
              </div>
            ) : (
              <>
                {filteredOrders.map((order, index) => {
                  const isExpanded = expandedOrderIndex === index;
                  return (
                    <div key={order._id} className="order-card">

                      {/* Card header */}
                      <div className="order-card-header">
                        <div className="order-card-icon">
                          <i className="fas fa-tags" style={{ color: "#ff5100", fontSize: 15 }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="order-card-title">{order.quantity?.toLocaleString()} tags</div>
                          <div className="order-card-sub">{order.date}</div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span className="order-pill order-pill-amber">
                            <i className="fas fa-clock" style={{ fontSize: 9 }} />
                            {order.status}
                          </span>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="order-desc-cell">
                        <div className="order-meta-label">
                          <i className="fas fa-align-left" /> Description
                        </div>
                        <div className="order-meta-value" style={{ fontWeight: 400, color: "#374151" }}>
                          {order.description}
                        </div>
                      </div>

                      {/* Metadata grid */}
                      {(() => {
                        const assignedQty = order.assignments.reduce(
                          (sum, a) => sum + (a.maxSerial - a.minSerial + 1), 0
                        );
                        const unassigned = order.quantity - assignedQty;
                        return (
                          <div className="order-meta-grid">
                            <div className="order-meta-cell">
                              <div className="order-meta-label">
                                <i className="fas fa-barcode" /> Serial Range
                              </div>
                              <div className="order-meta-value" style={{ fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace", fontSize: 12.5 }}>
                                {order.serialRange}
                              </div>
                            </div>
                            <div className="order-meta-cell">
                              <div className="order-meta-label">
                                <i className="fas fa-tag" /> Unassigned Tags
                              </div>
                              <div className="order-meta-value" style={{ color: unassigned === 0 ? "#059669" : unassigned < 0 ? "#dc2626" : "#111827" }}>
                                {unassigned.toLocaleString()}
                              </div>
                            </div>
                            <div className="order-meta-cell">
                              <div className="order-meta-label">
                                <i className="fas fa-expand-arrows-alt" /> Size
                              </div>
                              <div className="order-meta-value">
                                {order.type}{order.length !== "—" ? ` (${order.length})` : ""}
                              </div>
                            </div>
                            <div className="order-meta-cell">
                              <div className="order-meta-label">
                                <i className="fas fa-bullseye" /> Purpose
                              </div>
                              <div className="order-meta-value">{order.purpose}</div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Actions */}
                      <div className="order-card-actions">
                        <button
                          className={`btn-action btn-action-toggle${isExpanded ? " active" : ""}`}
                          onClick={() => toggleExpanded(index)}
                        >
                          <i className={`fas ${isExpanded ? "fa-chevron-up" : "fa-chevron-down"}`} style={{ fontSize: 11 }} />
                          {isExpanded ? "Hide Assignments" : `Assignments (${order.assignments.length})`}
                        </button>
                        <button
                          className="btn-action btn-action-assign"
                          onClick={() => handleCreateAssignment(index)}
                        >
                          <i className="fas fa-plus" style={{ fontSize: 11 }} />
                          Assign
                        </button>
                        <button
                          className="btn-action btn-action-invoice btn-action-ml"
                          onClick={() => navigate(`/irisinvoice?order=${order._id}`)}
                        >
                          <i className="fas fa-file-invoice-dollar" style={{ fontSize: 11 }} />
                          View Invoice
                        </button>
                      </div>

                      {/* Assignments panel */}
                      {isExpanded && (
                        <div className="assignments-panel">
                          {order.assignments.length > 0 ? (
                            <table className="assignments-table">
                              <thead>
                                <tr>
                                  <th>Serial Range</th>
                                  <th>Qty</th>
                                  <th>Client</th>
                                  <th>Description</th>
                                  <th>Date</th>
                                  <th>Pricing</th>
                                  <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {order.assignments.map((a) => (
                                  <tr key={a._id}>
                                    <td>
                                      <span className="serial-mono">
                                        {a.minSerial} – {a.maxSerial}
                                      </span>
                                    </td>
                                    <td style={{ color: "#374151", fontWeight: 600 }}>
                                      {(a.maxSerial - a.minSerial + 1).toLocaleString()}
                                    </td>
                                    <td>
                                      {a.client?.name ? (
                                        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                          {orgLogoMap[a.client.name] ? (
                                            <img
                                              src={orgLogoMap[a.client.name]}
                                              alt={a.client.name}
                                              style={{
                                                width: 20, height: 20, borderRadius: "50%",
                                                objectFit: "cover", flexShrink: 0,
                                                border: "1px solid rgba(0,0,0,0.08)",
                                              }}
                                            />
                                          ) : (
                                            <span style={{
                                              width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                                              background: "#f3f4f6", border: "1px solid #e5e7eb",
                                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                                            }}>
                                              <i className="fas fa-building" style={{ fontSize: 9, color: "#9ca3af" }} />
                                            </span>
                                          )}
                                          <span className="client-name">{a.client.name}</span>
                                        </div>
                                      ) : (
                                        <span style={{ color: "#9ca3af" }}>—</span>
                                      )}
                                    </td>
                                    <td style={{ color: "#6b7280" }}>{a.description || "—"}</td>
                                    <td style={{ whiteSpace: "nowrap", color: "#6b7280" }}>
                                      {new Date(a.date).toLocaleDateString("en-US", {
                                        year: "numeric", month: "short", day: "numeric",
                                      })}
                                    </td>
                                    <td>{renderPricing(a)}</td>
                                    <td style={{ textAlign: "right" }}>
                                      <div
                                        className="asgn-actions"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <button
                                          className="asgn-ellipsis"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenMenuId(openMenuId === a._id ? null : a._id);
                                          }}
                                        >
                                          <i className="fas fa-ellipsis-h" />
                                        </button>
                                        {openMenuId === a._id && (
                                          <div
                                            className="asgn-menu"
                                            role="menu"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <button
                                              className="asgn-menu-item danger"
                                              onClick={() => {
                                                setOpenMenuId(null);
                                                handleDeleteAssignment(a);
                                              }}
                                            >
                                              <i className="fas fa-trash-alt" /> Delete
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div className="assignments-empty">
                              <i className="fas fa-inbox" />
                              No assignments yet — click <strong>Assign</strong> above to create one.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {tagOrders.length === 0 ? (
                  <div style={{ textAlign: "center", color: "#9ca3af", marginTop: 48, fontSize: 14 }}>
                    <i className="fas fa-box-open" style={{ fontSize: 28, marginBottom: 10, display: "block" }} />
                    No orders yet.
                  </div>
                ) : (
                  filteredOrders.length === 0 && (
                    <div style={{ textAlign: "center", color: "#9ca3af", marginTop: 48, fontSize: 14 }}>
                      <i className="fas fa-search" style={{ fontSize: 28, marginBottom: 10, display: "block" }} />
                      No orders match "{searchTerm}".
                    </div>
                  )
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <CreateOrder
        domain={domain}
        show={showCreateOrderModal}
        handleClose={handleCloseCreateOrderModal}
        handleFormSubmit={handleFormSubmit}
      />
      <CreateOrderAssignment
        domain={domain}
        show={showCreateOrderAssignmentModal}
        handleClose={handleCloseCreateOrderAssignmentModal}
        handleFormSubmit={handleFormSubmit}
        orderId={selectedOrderId}
        modalData={modalData}
      />
      <DeleteOrderAssignmentModal
        show={showDeleteAssignmentModal}
        handleClose={() => setShowDeleteAssignmentModal(false)}
        assignmentId={deleteAssignment?._id}
        serialRange={deleteAssignment ? `${deleteAssignment.minSerial} - ${deleteAssignment.maxSerial}` : ""}
        clientName={deleteAssignment?.client?.name || ""}
        onDeleted={fetchOrders}
      />
    </>
  );
};

export default BleSTOrders;
